/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Castiel Zhang
 * Created: 13/10/23
 */

import React, { useEffect } from 'react';
import { Button, Flex, FormControl, FormLabel, HStack, Link, Select, Text } from '@chakra-ui/react';
import { useGetRequest } from '@/utils/useStatefulRequests';

import { useAssignment } from '@/components/contexts/AssignmentContext/useAssignment';
import useWorkplanVersion from '@/components/contexts/VersionContext/useWorkplanVersion';
import {
    mapWorkplanStageToString,
    mapWorkplanStateToString,
    Workplan,
    WorkplanYear,
} from '@/wpt-lib/interfaces/Workplan';
import useNavigateOptions from '@/components/contexts/NaviateContext/useNavigateOptions';
import { isNullish } from '@/wpt-lib/utils/isNullish';
import StaffAssignmentDropdown from '@/components/Header/StaffAssignmentDropdown';
import { ViewIcon } from '@chakra-ui/icons';

interface WorkplanSelectorProps {
    workplan: Workplan;
    readonly?: boolean;
}
export const WorkplanSelector = ({ workplan, readonly }: WorkplanSelectorProps) => {
    const { navigatePath, supervisor, admin } = useNavigateOptions();
    const { currentAssignment } = useAssignment();

    const { version, workplanVersion, loading } = useWorkplanVersion();

    const { makeRequest, responseData: workplanYears } = useGetRequest<WorkplanYear[]>();
    const getAllWorkPlans = async () => {
        try {
            await makeRequest(`workplan/years`, {
                params: {
                    assignment: workplan.assignment?.id,
                },
            });
        } catch {
            // Handled by hook.
        }
    };

    useEffect(() => {
        getAllWorkPlans();
    }, [currentAssignment?.id, workplan.assignment?.id]);

    const handleYearChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const selected = workplanYears?.find((w) => w.id === +event.target.value);
        if (selected) {
            navigatePath(selected.id, selected.versions[selected.versions.length - 1]);
        }
    };

    const handleVersionChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        navigatePath(workplan.id, +event.target.value);
    };

    const generateYearOptions = () => {
        if (workplanYears) {
            // Mapping to JSX elements
            return workplanYears.map((workplan) => (
                <option key={workplan.id} value={workplan.id}>
                    {workplan.year}
                    {workplan.historical ? ' (Historical)' : ''}
                </option>
            ));
        }
        return <option key="default"></option>;
    };

    const generateVersionOptions = () => {
        if (workplanYears) {
            const workplanYear = workplanYears.find((w) => w.id === workplan.id);

            if (workplanYear)
                return workplanYear.versions.map((version) => (
                    <option key={version} value={version}>
                        {version}
                    </option>
                ));
        }
        return <option></option>;
    };

    return (
        <>
            <FormControl display="flex" alignItems="baseline" width="unset">
                <FormLabel fontSize="lg" fontWeight="semibold" textColor="black">
                    Year
                </FormLabel>
                <Select
                    value={workplan.id}
                    onChange={handleYearChange}
                    disabled={readonly}
                    rounded="none"
                    role="combobox"
                >
                    {generateYearOptions()}
                </Select>
            </FormControl>
            <FormControl display="flex" alignItems="baseline" width="unset">
                <FormLabel fontSize="lg" fontWeight="semibold" textColor="black">
                    Version
                </FormLabel>
                <Select
                    value={version ?? workplan.currentVersion.version}
                    onChange={handleVersionChange}
                    minWidth="4em"
                    disabled={readonly}
                    rounded="none"
                    role="combobox"
                >
                    {generateVersionOptions()}
                </Select>
            </FormControl>
            {(supervisor || admin) && <StaffAssignmentDropdown workplan={workplan} readonly={readonly} />}
            <HStack>
                <Text fontSize="lg" fontWeight="semibold" textColor="black">
                    Status:
                </Text>
                <Flex p={2} height="40px" alignItems="center" justifyContent="center" whiteSpace="nowrap">
                    {isNullish(version) || version === workplan.currentVersion.version
                        ? mapWorkplanStateToString[workplan.state]
                        : 'Superseded'}
                </Flex>
                <Text fontSize="lg" fontWeight="semibold" textColor="black">
                    Stage:
                </Text>
                <Flex p={2} height="40px" alignItems="center" justifyContent="center" whiteSpace="nowrap">
                    {workplanVersion?.stage && !loading ? mapWorkplanStageToString[workplanVersion.stage] : ''}
                </Flex>
                <Button
                    variant="outline"
                    role="link"
                    leftIcon={<ViewIcon />}
                    rounded="none"
                    ml={0}
                    as={Link}
                    href="https://studentutsedu.sharepoint.com/:b:/s/UTSWorkPlanningTool/EY1L-AStPcNAiKUYFl_2MBQBbIVi925nNDuDzPD-A-idkA?e=nsUV1G"
                    target="_blank"
                    sx={{ _hover: { textDecoration: 'none', backgroundColor: 'gray.100' } }}
                >
                    View Workflow
                </Button>
            </HStack>
        </>
    );
};
